import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser, mapRoleLabelToApi, getUsers, ApiUser, mapApiRoleToLabel } from '../../services/UserService';

interface Permission {
  name: string;
  read: boolean;
  write: boolean;
}

interface ExistingUserRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  lastLogin?: string | null;
  status: 'active' | 'inactive';
}

const AddNewUserPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    department: '',
    mobile: '',
    email: '',
    company: '',
    role: '',
    temporaryPassword: ''
  });

  const [permissions, setPermissions] = useState<Permission[]>([
    { name: 'User Management', read: false, write: false },
    { name: 'Messaging', read: false, write: false },
    { name: 'Templates', read: false, write: false },
    { name: 'Billing', read: false, write: false },
    { name: 'Reports', read: false, write: false },
  ]);

  const [existingUsers, setExistingUsers] = useState<ExistingUserRow[]>([]);
  const [loadingExisting, setLoadingExisting] = useState<boolean>(false);
  const [existingError, setExistingError] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    setLoadingExisting(true);
    getUsers()
      .then((list) => {
        if (!mounted) return;
        const rows: ExistingUserRow[] = list.map((u: ApiUser) => ({
          id: u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.companyEmail,
          role: mapApiRoleToLabel(u.role),
          lastLogin: u.lastLogin || null,
          status: 'active'
        }));
        setExistingUsers(rows);
      })
      .catch((e) => { if (mounted) setExistingError(e.message || 'Failed to load users'); })
      .finally(() => { if (mounted) setLoadingExisting(false); });
    return () => { mounted = false; };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePermissionChange = (index: number, type: 'read' | 'write') => {
    const updatedPermissions = [...permissions];
    updatedPermissions[index][type] = !updatedPermissions[index][type];
    setPermissions(updatedPermissions);
  };

  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSave = async () => {
    setError('');
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile) {
      setError('Please fill in First Name, Last Name, Email and Mobile.');
      return;
    }
    setSaving(true);
    try {
      await createUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyEmail: formData.email,
        contactNumber: formData.mobile,
        department: formData.department,
        company: formData.company,
        role: mapRoleLabelToApi(formData.role),
        temporaryPassword: formData.temporaryPassword || undefined
      });
      navigate('/superadmin/users-roles', { replace: true, state: { userCreated: true } });
    } catch (e: any) {
      setError(e.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Add New User</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/users-roles')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save User'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-red-600 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* User Information */}
        <div>
          <h2 className="text-lg font-medium mb-4">User Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temporary Password (optional)
              </label>
              <input
                type="password"
                name="temporaryPassword"
                value={formData.temporaryPassword}
                onChange={handleInputChange}
                placeholder="Enter a temporary password for first login"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
              <p className="mt-1 text-xs text-gray-500">If left blank, a secure temporary password will be generated by the system.</p>
            </div>
          </div>
        </div>

        {/* Role Selection */}
        <div>
          <h2 className="text-lg font-medium mb-4">User Role</h2>
          <div className="space-y-3">
            {['Super Admin', 'Admin', 'Manager', 'User', 'Demo'].map((role) => (
              <div key={role} className="flex items-center">
                <input
                  type="radio"
                  id={role}
                  name="role"
                  value={role}
                  checked={formData.role === role}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-[#FF5722] border-gray-300 focus:ring-[#FF5722]"
                />
                <label htmlFor={role} className="ml-2 text-sm text-gray-700">
                  {role}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Permissions */}
        <div>
          <h2 className="text-lg font-medium mb-4">Permissions</h2>
          <div className="space-y-4">
            {permissions.map((permission, index) => (
              <div key={permission.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium">{permission.name}</span>
                <div className="space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={permission.read}
                      onChange={() => handlePermissionChange(index, 'read')}
                      className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                    />
                    <span className="ml-2 text-sm text-gray-600">Read</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={permission.write}
                      onChange={() => handlePermissionChange(index, 'write')}
                      className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                    />
                    <span className="ml-2 text-sm text-gray-600">Write</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Existing Users List */}
        <div>
          <h2 className="text-lg font-medium mb-4">Existing Users</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingExisting && (
                  <tr>
                    <td className="px-6 py-4" colSpan={5}>Loading...</td>
                  </tr>
                )}
                {!loadingExisting && existingError && (
                  <tr>
                    <td className="px-6 py-4 text-red-600" colSpan={5}>{existingError}</td>
                  </tr>
                )}
                {!loadingExisting && !existingError && existingUsers.length === 0 && (
                  <tr>
                    <td className="px-6 py-4" colSpan={5}>No users found.</td>
                  </tr>
                )}
                {!loadingExisting && !existingError && existingUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium">{`${user.firstName} ${user.lastName}`}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{user.role}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-400">—</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{user.lastLogin ? user.lastLogin : '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewUserPage; 